import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Price as DreamPrice, Tier as DreamTier } from '@shared/dream-components/src';

import useCreateStripePremiumSession from '~/hooks/useCreateStripePremiumSession';
import useStaticBillingDetails from '~/hooks/useLoaders/useStaticBillingDetails';
import useStaticPricingDetails from '~/hooks/useLoaders/useStaticPricingDetails';
import useStaticTiersBillingSummary from '~/hooks/useLoaders/useStaticTiersBillingSummary';
import type { Site } from '~/interfaces/site';
import { type Price, PriceInterval, type Subscription } from '~/interfaces/subscriptions';
import isPremiumPurchasable from '~/utils/isPremiumPurchasable';
import toast from '~/utils/toast';

import subscriptionStatusBadgeType from '../../../_utils/subscriptionStatusBadgeType';

const currencyFormatter = ({ locale = 'en-US', currency = 'USD' }: { locale?: string; currency?: string }) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    trailingZeroDisplay: 'stripIfInteger',
  } as Intl.NumberFormatOptions);

const formatPrice = (currency: string, amountCents: number, denominator: string) =>
  `${currencyFormatter({ currency }).format((amountCents || 0) / 100)}/${denominator}`;

type BadgeType = 'success' | 'warning' | 'alert' | 'information';
export interface CurrentPlan {
  id?: string;
  name: string;
  features: string[];
  price?: string;
  originalPrice?: string;
  isDiscounted: boolean;
  isGift: boolean;
  badgeType?: BadgeType;
  tooltipText?: string;
  status: string;
  isPaused: boolean;
  isDowngrading: boolean;
  downgradesAt?: string;
  isPermanent: boolean;
  isFree: boolean;
  tierName?: string;
  canDowngrade: boolean;
  canChangePlan: boolean;
  canPausePlan: boolean;
  canUnpausePlan: boolean;
  tier?: DreamTier;
  groupManagedBy?: string | null;
  accessEndsAt?: string | null;
}

export const useCurrentPlan = ({
  isLoggedIn,
  site,
  subscription,
  subscriberId,
  groupSubscriptionsEnabled,
}: {
  site: Site;
  subscription: Subscription;
  subscriberId: string;
  isLoggedIn: boolean;
  groupSubscriptionsEnabled: boolean;
}) => {
  const { t, i18n } = useTranslation();

  // Subscription status checks
  const isInactive = subscription.status === 'inactive';
  const isPaused = subscription.status === 'paused';
  const isFreeTier = Array.isArray(subscription.premium_tiers) && subscription.premium_tiers.length === 0;
  const isPremiumTier = Array.isArray(subscription.premium_tiers) && subscription.premium_tiers.length > 0;
  const hasActivePaidTier =
    Array.isArray(subscription.premium_tiers) &&
    subscription.premium_tiers.some((tier) => !tier.paused && !tier.is_downgrading);
  const isPermanentlyUpgraded = subscription.premium_tiers.some((tier) => tier.permanent);

  const isInUnsubscribableStatus = ['active', 'pending', 'paused'].includes(subscription.status);
  const isPremiumWithoutActiveBilling = isPremiumTier && !hasActivePaidTier;
  const isLoggedInPremiumSubscriber = isPremiumTier && isLoggedIn;

  const canUnsubscribe =
    isInUnsubscribableStatus && (isFreeTier || isPremiumWithoutActiveBilling || isLoggedInPremiumSubscriber);
  const canDowngrade = isPremiumTier && isLoggedIn && !isPermanentlyUpgraded;

  const {
    billingSummary,
    isLoading: isLoadingBillingSummary,
    refetch: refetchBillingSummary,
  } = useStaticTiersBillingSummary({
    enabled: isPremiumTier && isLoggedIn && !!subscription?.stripe_customer_id,
    subscriberId,
  });
  const { data: billingDetails, isLoading: isLoadingBillingDetails } = useStaticBillingDetails({
    enabled: isPremiumTier && isLoggedIn && !!subscription?.stripe_customer_id,
    subscriberId,
  });
  const { data: pricingDetails, isLoading: isLoadingPricingDetails } = useStaticPricingDetails({
    enabled: isPremiumTier,
    subscriberId,
  });

  const tierPricesById = useMemo(() => {
    return (
      pricingDetails?.tiers?.reduce(
        (acc, tier) => {
          tier.prices.forEach((price) => {
            acc[price.id] = price;
          });
          return acc;
        },
        {} as Record<string, Price>,
      ) || {}
    );
  }, [pricingDetails?.tiers]);

  const isPendingDowngrade = billingSummary?.pending_downgrade || false;
  const isProratedDowngradeAvailable = billingSummary?.prorated_downgrade_available || false;
  const publicationHasPremiumEnabled = isPremiumPurchasable(site?.publication);
  const canUpgrade = publicationHasPremiumEnabled && isFreeTier && !isInactive;

  const allTiers = pricingDetails?.tiers || [];
  const availableTiers = useMemo(() => {
    const filteredTiers = allTiers
      .filter(
        (tier) =>
          tier.prices.filter((price) => ![PriceInterval.DONATION, PriceInterval.ONE_TIME].includes(price.interval))
            .length > 0,
      )
      .map(
        (tier) =>
          ({
            ...tier,
            prices: tier.prices
              .filter((price) => ![PriceInterval.DONATION, PriceInterval.ONE_TIME].includes(price.interval))
              .map((price) => {
                return {
                  ...price,
                  formatted_amount: formatPrice(price.currency, price.amount_cents, price.denominator),
                } as DreamPrice;
              }),
          }) as DreamTier,
      );

    return filteredTiers;
  }, [allTiers]);
  const subscriptionTierIds = useMemo(() => {
    return subscription.premium_tiers?.map((tier) => tier.id) || [];
  }, [subscription.premium_tiers]);
  const subscriptionPriceIds = useMemo(() => {
    return subscription.premium_tiers?.map((tier) => tier.tier_price_id) || [];
  }, [subscription.premium_tiers]);

  const { private: isPublicationIsPrivate, require_subscriber_approval: doesPublicationRequiresSubscriberApproval } =
    site?.publication || {};

  const resubscribeMessage = t('account_actions.resubscribe.description');

  const downgradeMessage = useMemo(() => {
    if (!isLoggedIn) {
      return t('account_actions.downgrade.unsubscribe_login_description');
    }

    if (isPublicationIsPrivate) {
      return t('account_actions.downgrade.unsubscribe_is_private');
    }

    if (doesPublicationRequiresSubscriberApproval) {
      return t('account_actions.downgrade.unsubscribe_requires_approval');
    }

    if (canDowngrade && !isPendingDowngrade) {
      if (subscription.premium_tiers.some((tier) => tier.paid_trial_info)) {
        return t('account_actions.downgrade.cancel_paid_trial_description');
      }
      if (isProratedDowngradeAvailable) {
        return t('account_actions.downgrade.downgrade_prorated_description');
      }
      return t('account_actions.downgrade.downgrade_immediately_description');
    }
  }, [
    isLoggedIn,
    isPublicationIsPrivate,
    isProratedDowngradeAvailable,
    isPendingDowngrade,
    doesPublicationRequiresSubscriberApproval,
    canDowngrade,
    subscription.premium_tiers,
  ]);

  const { createSession, isLoading: isCreatingStripeSession } = useCreateStripePremiumSession({
    isPremium: isPremiumTier,
    subscriberId,
    isLoggedIn,
    stripeCustomerId: subscription.stripe_customer_id,
  });

  const onClickUpdateBilling = async () => {
    let toastId: string | number | undefined;
    try {
      toastId = toast.loading(t('billing.details.toast.creating_premium_session'));

      const premiumSessionUrl = await createSession();
      if (!premiumSessionUrl) {
        toast.dismiss(toastId);
        toast.error(t('billing.details.toast.create_premium_session_error'));
        return;
      }

      window.location.href = premiumSessionUrl;
    } catch {
      toast.dismiss(toastId);
      toast.error(t('billing.details.toast.create_premium_session_error'));
      return;
    }
  };

  const currentPlans: CurrentPlan[] = useMemo(() => {
    if (isFreeTier) {
      return [
        {
          name: t('billing.free_plan.title'),
          description: '',
          features: [],
          price: undefined,
          status: isPaused ? t('manage.account_profile.paused_badge.label') : subscription.status,
          badgeType: canUnsubscribe && !isPaused ? 'success' : 'information',
          tooltipText:
            isPaused && subscription?.pause_info
              ? `${t('billing.current_plan.pause_tooltip_prefix')} ${new Date(subscription.pause_info.scheduled_resume_at).toLocaleDateString()}`
              : '',
          isDowngrading: false,
          isDiscounted: false,
          isGift: false,
          isPermanent: false,
          canDowngrade: false,
          canPausePlan: subscription.status === 'active',
          canUnpausePlan: isPaused,
          canChangePlan: false,
          isPaused: false,
          isFree: true,
        },
      ];
    }

    return subscription.premium_tiers?.map((subscriptionTier) => {
      const isGroupManaged = !!subscriptionTier.group_managed_by;

      const nameWithGiftDetails = subscriptionTier.gift_details
        ? `${subscriptionTier.name} - ${subscriptionTier.gift_details}`
        : subscriptionTier.name;

      const pricePreview = subscriptionTier.price_preview;

      // price_preview recomputes the discount from today's offer eligibility and so ignores the
      // coupon's duration. Its denominator is the publisher's label; next_invoice.cadence is raw.
      const nextInvoice = billingSummary?.[subscriptionTier.id]?.next_invoice;
      const amountCents = nextInvoice?.recurring_amount_cents ?? pricePreview?.amount_cents;
      const originalAmountCents =
        nextInvoice?.recurring_amount_cents === undefined
          ? pricePreview?.original_amount_cents
          : nextInvoice.original_amount_cents;

      const price =
        pricePreview && amountCents !== undefined
          ? formatPrice(pricePreview.currency, amountCents, pricePreview.denominator)
          : undefined;

      const originalPrice =
        pricePreview && originalAmountCents !== undefined
          ? formatPrice(pricePreview.currency, originalAmountCents, pricePreview.denominator)
          : undefined;

      const tier = availableTiers.find((tier) => tier.id === subscriptionTier.id);

      const isTierPaused = Boolean(subscriptionTier.paused);
      const pauseTooltipText =
        isTierPaused && subscriptionTier.pause_info
          ? `${t('billing.current_plan.pause_tooltip_prefix')} ${new Date(subscriptionTier.pause_info.scheduled_resume_at).toLocaleDateString()}`
          : undefined;
      const downgradeTooltipText =
        subscriptionTier.is_downgrading && subscriptionTier.cancel_at
          ? `${t('billing.current_plan.cancel_tooltip_prefix')} ${new Date(subscriptionTier.cancel_at).toLocaleDateString()}`
          : undefined;
      const trialingStatusText = subscriptionTier.is_trialing
        ? t('manage.account_profile.trialing_badge.label')
        : undefined;
      const pausedStatusText = subscriptionTier.paused ? t('manage.account_profile.paused_badge.label') : undefined;
      const downgradingStatusText = subscriptionTier.is_downgrading
        ? t('billing.current_plan.cancel_badge_label')
        : undefined;
      const giftStatusText = subscriptionTier.is_gift ? t('billing.current_plan.gift_badge_label') : undefined;

      const badgeType = (() => {
        if (subscriptionTier.is_trialing) return 'success';
        if (subscriptionTier.paused) return 'information';
        if (subscriptionTier.is_downgrading && !subscriptionTier.is_gift) return 'warning';
        if (subscriptionTier.is_gift) return 'information';
        return subscriptionStatusBadgeType(subscription.status);
      })();

      return {
        id: subscriptionTier.id,
        name: nameWithGiftDetails,
        tierName: subscriptionTier.name,
        features: subscriptionTier?.price_preview?.features || [],
        price: !subscriptionTier.is_trialing ? price : undefined,
        originalPrice: originalPrice,
        status:
          trialingStatusText || pausedStatusText || downgradingStatusText || giftStatusText || subscription.status,
        badgeType,
        isPaused: isTierPaused,
        tooltipText: pauseTooltipText || downgradeTooltipText,
        isDiscounted: !!(originalPrice && price && originalPrice !== price),
        isGift: subscriptionTier.is_gift,
        isDowngrading: subscriptionTier.is_downgrading,
        downgradesAt: subscriptionTier.cancel_at ?? undefined,
        canDowngrade:
          !isGroupManaged &&
          !isInactive &&
          !subscriptionTier.paused &&
          !subscriptionTier.is_downgrading &&
          !subscriptionTier.permanent &&
          isLoggedIn,
        canChangePlan:
          !isGroupManaged &&
          !subscriptionTier.is_archived &&
          Array.isArray(tier?.prices) &&
          tier.prices.length > 1 &&
          Boolean(
            !subscriptionTier.paused &&
              !subscriptionTier.is_downgrading &&
              !subscriptionTier.permanent &&
              subscriptionTier.price_preview?.interval &&
              [PriceInterval.MONTH, PriceInterval.QUARTER, PriceInterval.YEAR].includes(
                subscriptionTier.price_preview.interval,
              ) &&
              isLoggedIn,
          ),
        canPausePlan:
          !isGroupManaged &&
          !isPaused &&
          Boolean(
            !subscriptionTier.paused && !subscriptionTier.is_downgrading && !subscriptionTier.permanent && isLoggedIn,
          ),
        canUnpausePlan:
          !isGroupManaged &&
          !isPaused &&
          Boolean(
            subscriptionTier.paused && !subscriptionTier.is_downgrading && !subscriptionTier.permanent && isLoggedIn,
          ),
        isPermanent: subscriptionTier.permanent,
        tier,
        isFree: false,
        groupManagedBy:
          groupSubscriptionsEnabled && subscriptionTier.group_managed_by
            ? t(
                // An admin occupying one of their own seats is told to contact the group
                // administrator by the member copy — that is themselves.
                subscriptionTier.group_managed_by.viewer_is_admin
                  ? 'billing.current_plan.group_managed_by_admin'
                  : 'billing.current_plan.group_managed_by',
                {
                  groupName: subscriptionTier.group_managed_by.group_name,
                  adminEmail: subscriptionTier.group_managed_by.admin_email,
                  interpolation: { escapeValue: false },
                },
              )
            : null,
        accessEndsAt:
          groupSubscriptionsEnabled && subscriptionTier.group_managed_by && subscriptionTier.group_ends_at
            ? t('billing.current_plan.access_ends_on', {
                date: new Date(subscriptionTier.group_ends_at).toLocaleDateString(i18n.language),
                interpolation: { escapeValue: false },
              })
            : null,
      };
    });
  }, [
    tierPricesById,
    isFreeTier,
    isInactive,
    subscription,
    availableTiers?.length,
    billingSummary,
    canUnsubscribe,
    isLoggedIn,
    groupSubscriptionsEnabled,
  ]);

  const areAllPlansDowngrading = currentPlans.every((plan) => plan.isDowngrading);
  const areAllPlansGifts = currentPlans.every((plan) => plan.isGift);

  const unsubscribeMessage = useMemo(() => {
    if (isPendingDowngrade || !isPremiumTier) {
      return t('account_actions.downgrade.unsubscribe_free_description');
    }

    return t('account_actions.downgrade.unsubscribe_premium_description');
  }, [isPendingDowngrade, isPremiumTier]);

  return {
    areAllPlansGifts,
    areAllPlansDowngrading,
    isInactive,
    isPaused,
    canUpgrade,
    canUnsubscribe,
    onClickUpdateBilling,
    downgradeMessage,
    resubscribeMessage,
    unsubscribeMessage,
    showEmptyPlanState: !isFreeTier && !publicationHasPremiumEnabled,
    isLoading: isLoadingBillingSummary || isLoadingBillingDetails || isLoadingPricingDetails,
    subscriptionPriceIds,
    subscriptionTierIds,
    isFreeTier,
    isPremiumTier,
    hasActivePaidTier,
    currentPlans,
    isLoadingBillingSummary,
    isCreatingStripeSession,
    billingDetails,
    billingSummary,
    refetchBillingSummary,
  };
};
